import React from "react";
import {
  isChunkLoadError,
  reloadOnceForStaleChunks,
} from "../lib/staleChunkRecovery";

interface AppErrorBoundaryProps {
  children: React.ReactNode;
}

interface AppErrorBoundaryState {
  error: Error | null;
}

/**
 * Top-level error boundary.
 *
 * Its most important job is recovering from failed dynamic imports: every
 * route is React.lazy-loaded, so when a deploy replaces the hashed chunk
 * files (prod) or Vite re-optimizes dependencies mid-session (dev), the next
 * client-side navigation rejects the import and — without this boundary —
 * the app would be stuck on the Suspense spinner until a manual hard refresh.
 *
 * For chunk-load failures we transparently reload the page once (guarded so
 * a truly broken deploy cannot cause a reload loop). For any other render
 * error we show a minimal recovery UI instead of a dead screen.
 */
export default class AppErrorBoundary extends React.Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error) {
    if (isChunkLoadError(error)) {
      // New deploy (or dev re-optimization) invalidated the loaded chunks —
      // a single reload fetches the fresh index.html and chunk manifest.
      reloadOnceForStaleChunks();
    } else {
      console.error("[AppErrorBoundary] Uncaught render error:", error);
    }
  }

  render() {
    if (this.state.error) {
      const chunkError = isChunkLoadError(this.state.error);
      return (
        <div className="w-full h-screen flex items-center justify-center bg-neutral-900 text-white p-4">
          <div className="w-full max-w-md text-center p-8 bg-neutral-800/80 rounded-xl border border-neutral-700/80 shadow-2xl space-y-4">
            <h1 className="text-lg font-semibold">
              {chunkError ? "New version available" : "Something went wrong"}
            </h1>
            <p className="text-sm text-neutral-400">
              {chunkError
                ? "The application was updated. Please reload to get the latest version."
                : "An unexpected error occurred. Reloading usually fixes this."}
            </p>
            <button
              className="px-4 py-2 rounded-md bg-amber-600 hover:bg-amber-500 text-sm font-medium"
              onClick={() => window.location.reload()}
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
